import React from 'react';
import { Link } from 'react-router-dom';
import { Button, Progress, Table, Tag } from 'antd';
import { CarOutlined, FileTextOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { WZDocumentDto } from '../../../../dto/wz';
import type { OrderWarehouseLine } from '../../../../types/orderWarehouseSummary';
import type { OrderProduct } from '../../../../types/order';
import { formatCurrencyLocalized } from '../../../../utils/currency';
import { formatDateEuropean } from '../../../../utils/dateUtils';
import { formatProductDimensions } from '../../../../utils/productDimensions';
import { getOrderProductDisplayDimensions } from '../../orderProductDisplay';
import type { OrderStepContext } from '../orderStepContext';
import { Callout, HiddenValue, StepSection } from '../OrderProcessParts';
import { issuePercent, OrderStockTable, OrderLineNameWithDimensions, useQuantityFormatter } from '../OrderProcessTables';
import OrderTransportSummary, { activeOrderTransports } from '../OrderTransportSummary';

const WZ_STATUS_COLORS: Record<string, string> = { DRAFT: 'default', CONFIRMED: 'blue', RELEASED: 'green', RECEIVED: 'green', CANCELLED: 'red' };

/**
 * One WZ as a delivery batch (makieta `wzCard`): lines, issue date, cost of the WZ for cost readers.
 * Line dimensions: the WZ line's own, otherwise its source order line (`products`).
 */
export function WzBatchCard({ document, index, canViewCosts, products = [] }: {
  document: WZDocumentDto; index: number; canViewCosts: boolean; products?: OrderProduct[];
}) {
  const { t, i18n } = useTranslation(['orders', 'wz']);
  const quantity = useQuantityFormatter();
  const items = document.items ?? [];
  const productsById = new Map(products.map((product) => [product.id, product]));
  return (
    <div className="zk-wz" data-testid="zk-wz-card">
      <div className="zk-wz__head">
        <FileTextOutlined aria-hidden="true" />
        <Link className="zk-wz__number" to={`/app/wz/${encodeURIComponent(document.id)}`}>{document.wzNumber}</Link>
        <Tag color="blue">{t('orders:process.delivery.batch', { number: index + 1 })}</Tag>
        <Tag color={WZ_STATUS_COLORS[document.status] || 'default'}>{t(`wz:status.${document.status.toLowerCase()}`)}</Tag>
        <span className="zk-wz__meta">{t('orders:process.delivery.issueDate', { date: formatDateEuropean(document.issueDate) })}</span>
      </div>
      <div className="zk-wz__body">
        <div className="zk-source">
          {items.map((item) => {
            const dimensions = formatProductDimensions(item)
              ?? getOrderProductDisplayDimensions(item.sourceOrderProductId ? productsById.get(item.sourceOrderProductId) : null);
            return (
              <div className="zk-line-identity" key={item.id}>
                <span>{item.productName}: {quantity(item.quantity, item.unit)}</span>
                {dimensions && <small data-testid="zk-wz-line-dimensions">{dimensions}</small>}
              </div>
            );
          })}
        </div>
        <div className="zk-num">
          {canViewCosts
            ? (document.costOfGoodsSold == null
              ? t('orders:process.delivery.costPending')
              : t('orders:process.delivery.cost', { amount: formatCurrencyLocalized(document.costOfGoodsSold, 'PLN', i18n.language) }))
            : <HiddenValue reason="costs" />}
        </div>
      </div>
    </div>
  );
}

/**
 * W dostawie (makieta `sDost`): every WZ is a delivery batch; per line the
 * ordered / issued / remaining quantity, the progress and the issue state in
 * one table; „Wystaw kolejne WZ” opens the issue window (#266). WZ never carry
 * sale prices. Transport sits under the lines (left) so the WZ list, which grows with
 * every batch, has the right column to itself.
 */
export default function DeliveryStep({ ctx, onIssueWz }: { ctx: OrderStepContext; onIssueWz: () => void }) {
  const { t } = useTranslation(['orders']);
  const quantity = useQuantityFormatter();
  const { caps, readOnly, facts } = ctx;
  const lines = ctx.warehouse?.lines ?? null;
  const documents = (ctx.wzDocuments ?? []).filter((document) => document.status !== 'CANCELLED');
  const remaining = lines ? lines.some((line) => (line.remainingQuantity ?? 0) > 0) : !facts.allIssued;
  const trucks = activeOrderTransports(ctx.transports);
  const products = ctx.order.productsLoaded ? ctx.order.products : [];
  const productsById = new Map(products.map((product) => [product.id, product]));

  return (
    <div className="zk-grid" data-testid="zk-step-DELIVERY">
      <div className="zk-col">
        <StepSection title={t('orders:process.delivery.linesTitle')}>
          <Callout tone="info" flush>{t('orders:process.delivery.noSalePrices')}</Callout>
          {lines ? (
            <Table<OrderWarehouseLine>
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={lines}
              columns={[
                {
                  title: t('orders:process.lines.product'),
                  key: 'name',
                  render: (_v, line) => (
                    <OrderLineNameWithDimensions name={line.name} product={productsById.get(line.id)} testId="zk-delivery-line-dimensions" />
                  ),
                },
                { title: t('orders:process.delivery.ordered'), key: 'ordered', align: 'right', render: (_v, line) => <span className="zk-num">{quantity(line.orderedQuantity, line.unit)}</span> },
                { title: t('orders:process.delivery.issued'), key: 'issued', align: 'right', render: (_v, line) => <span className="zk-num">{quantity(line.issuedQuantity, line.unit)}</span> },
                { title: t('orders:process.delivery.remaining'), key: 'remaining', align: 'right', render: (_v, line) => <span className="zk-num">{quantity(line.remainingQuantity, line.unit)}</span> },
                {
                  title: t('orders:process.delivery.progress'),
                  key: 'progress',
                  width: 140,
                  render: (_v, line) => <Progress percent={issuePercent(line)} size="small" aria-label={line.name} />,
                },
                {
                  title: t('orders:process.delivery.state'),
                  key: 'state',
                  render: (_v, line) => (line.remainingQuantity === 0
                    ? <Tag color="green">{t('orders:process.delivery.lineIssued')}</Tag>
                    : (line.issuedQuantity ?? 0) > 0
                      ? <Tag color="orange">{t('orders:process.delivery.linePartial')}</Tag>
                      : <Tag>{t('orders:process.delivery.lineOpen')}</Tag>),
                },
              ]}
            />
          ) : (
            <>
              <OrderStockTable stockStatus={ctx.stockStatus} products={products} />
              {!caps.canReadWarehouse && <Callout tone="muted">{t('orders:process.delivery.noWarehouseAccess')}</Callout>}
            </>
          )}
        </StepSection>
        <StepSection
          title={t(ctx.order.deliveryMode === 'CUSTOMER_PICKUP' ? 'orders:process.delivery.pickupTitle' : 'orders:process.delivery.transportTitle')}
          icon={<CarOutlined />}
        >
          {ctx.order.deliveryMode === 'CUSTOMER_PICKUP'
            ? <Callout tone="muted" flush>{t('orders:process.transport.pickup')}</Callout>
            : trucks.length
              ? <OrderTransportSummary transports={trucks} canViewCosts={caps.canViewTransportCosts} />
              : <Callout tone="muted" flush>{caps.canViewTransports ? t('orders:process.transport.toPlan') : t('orders:process.transport.ourDelivery')}</Callout>}
        </StepSection>
      </div>
      <div className="zk-col">
        <StepSection
          title={t('orders:process.delivery.wzTitle')}
          icon={<FileTextOutlined />}
          testId="zk-wz-list"
          aside={!readOnly && caps.canManageWz && remaining && (
            <Button type="primary" size="small" icon={<PlusOutlined />} onClick={onIssueWz}>
              {documents.length ? t('orders:process.delivery.nextWz') : t('orders:process.delivery.firstWz')}
            </Button>
          )}
        >
          {!caps.canReadWarehouse
            ? <Callout tone="muted" flush>{t('orders:process.delivery.noWarehouseAccess')}</Callout>
            : documents.length
              ? documents.map((document, index) => (
                <WzBatchCard key={document.id} document={document} index={index} canViewCosts={caps.canViewWarehouseCosts} products={products} />
              ))
              : <Callout tone="muted" flush>{t('orders:process.delivery.noWz')}</Callout>}
          {caps.canReadWarehouse && remaining && documents.length > 0 && (
            <Callout tone="warning">{t('orders:process.delivery.remainingHint')}</Callout>
          )}
        </StepSection>
      </div>
    </div>
  );
}
